import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useSaveVacation, type User, type Vacation } from "@/api/queries";
import { useAuth } from "@/app/auth";
import { Field } from "@/components/common/Field";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import { Input, NativeSelect } from "@/components/ui/input";
import { errorText, fieldError } from "@/lib/errors";
import { dateInZone } from "@/lib/format";

type Person = { id: string; full_name: string };

type VacationDialogProps = {
  /** Whose vacation is edited; with `people` the person is chosen in the dialog. */
  person?: Person;
  people?: Person[];
  initial?: Vacation;
  onClose: () => void;
};

/** Sets the vacation dates of a person; the end date is optional. */
export function VacationDialog({ person, people, initial, onClose }: VacationDialogProps) {
  const { t } = useTranslation();
  const auth = useAuth();
  const me = auth.status === "authenticated" ? auth.me : null;
  const today = dateInZone(new Date(), me?.user.timezone);
  const [userId, setUserId] = useState(person?.id ?? people?.[0]?.id ?? "");
  const [from, setFrom] = useState(initial?.from ?? today);
  const [until, setUntil] = useState(initial?.until ?? "");
  const save = useSaveVacation();
  const self = userId === me?.user.id;
  const valid = Boolean(userId && from) && (!until || until >= from);

  const apply = (user: User) => {
    if (me && user.id === me.user.id) auth.setMe({ ...me, user });
  };
  const submit = async () => {
    apply(await save.mutateAsync({ userId, from, until: until || undefined }));
    onClose();
  };
  const name = person?.full_name ?? people?.find((p) => p.id === userId)?.full_name ?? "";
  const known = ["from", "until"].some((f) => fieldError(t, save.error, f));

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={initial ? t("vacations.dialog.editTitle") : t("vacations.dialog.title")}
        description={
          self ? t("vacations.dialog.selfDescription") : t("vacations.dialog.description", { name })
        }
        className="w-[min(92vw,440px)]"
      >
        <form
          className="flex flex-col gap-4 p-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) void submit().catch(() => undefined);
          }}
        >
          {people && !person && (
            <Field label={t("vacations.dialog.employee")}>
              <NativeSelect value={userId} onChange={(e) => setUserId(e.target.value)}>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.full_name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("vacations.dialog.from")} error={fieldError(t, save.error, "from")}>
              <Input type="date" value={from} required onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label={t("vacations.dialog.until")} error={fieldError(t, save.error, "until")}>
              <Input
                type="date"
                value={until}
                min={from > today ? from : today}
                onChange={(e) => setUntil(e.target.value)}
              />
            </Field>
          </div>
          <p className="-mt-2 text-xs text-muted">{t("vacations.dialog.untilHint")}</p>
          {save.error !== null && !known && <Alert variant="danger">{errorText(t, save.error)}</Alert>}
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                {t("common.cancel")}
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!valid || save.isPending}>
              {t("vacations.dialog.save")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
